<template>
    <div class="flex-grow-1 w-100 h-100" ref="root">
        <LMap v-if="nearViewport" :zoom="zoom" :center="center" :options="{zoomSnap: 0.25}" @ready="onMapReady" ref="container">
            <LControlLayers position="topright" @ready="onLayersControlReady"></LControlLayers>
            <template v-if="viewFitted">
                <MapyCzBasicLayer/>
                <MapyCzOrthoPhotoLayer :visible="false"/>
            </template>
            <LMarker v-for="(marker, index) in markers"
                     :key="index"
                     :icon="createIcon(marker)"
                     :lat-lng="marker.coordinates"
                     >
                <LPopup :options="{maxHeight: maxPopupHeight}">
                    <ul class="list-group">
                        <li v-if="marker.pointsToMainEstate" class="list-group-item border-0 p-0 py-3">
                            <span class="badge bg-info m-0 mb-2" v-if="comparedEstates.length > 0">Oceňovaná nemovitost</span>
                            <span class="d-block" v-html="mainEstate.title"></span>
                        </li>
                        <li v-for="title in marker.titles" class="list-group-item border-top-0 border-start-0 border-end-0 p-0 m-0 py-3" v-html="title"></li>
                    </ul>
                </LPopup>
            </LMarker>
        </LMap>
    </div>
</template>
<script lang="ts">
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {Place} from '@/components/maps/Place';
import L from 'leaflet';
import {DivIcon, LatLngBounds, LatLngTuple} from 'leaflet';
import {computed, defineComponent, onBeforeUnmount, onMounted, PropType, ref} from 'vue';
import {LControlLayers, LMap, LMarker, LPopup} from '@vue-leaflet/vue-leaflet';
import {publishLeafletMap, unpublishLeafletMap} from '@/shared/leafletMapRegistry';
import {whenNearViewport} from '@/shared/nearViewport';

interface EstatesMarker {
    pointsToMainEstate: boolean;
    titles: string[];
    className: string;
    coordinates: LatLngTuple;
}

export default defineComponent({
    components: {LPopup, MapyCzBasicLayer, MapyCzOrthoPhotoLayer, LControlLayers, LMap, LMarker},
    props: {
        mainEstate: {
            type: Object as PropType<Place | null>,
            default: null,
        },
        comparedEstates: {
            type: Array as PropType<Place[]>,
            default: () => [],
        },
    },
    setup(props) {
        const root = ref<HTMLElement | null>(null);
        const container = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const maxPopupHeight = ref<number | null>(null);
        // The tiles of the map are paid for, so the map is created only once it is about to come
        // into view: a visitor who never scrolls to it (or never opens the panel or the dialog it
        // sits in) loads no tiles at all.
        const nearViewport = ref(false);
        // The tile layers wait for the view of the estates, so no tiles of the initial view are loaded.
        const viewFitted = ref(false);
        let stopWatchingViewport: (() => void) | null = null;

        const markers = computed((): EstatesMarker[] => {
            const markersMap: {[coordinates: string]: EstatesMarker} = {};

            props.comparedEstates.forEach(estate => {
                const key = JSON.stringify(estate.coordinates);

                if (! (key in markersMap)) {
                    markersMap[key] = {
                        titles: [estate.title],
                        coordinates: estate.coordinates,
                        className: 'compared-estate-marker',
                        pointsToMainEstate: false,
                    };

                    return;
                }

                markersMap[key].titles.push(estate.title);
            });

            if (props.mainEstate === null) {
                return Object.values(markersMap);
            }

            const mainEstateKey = JSON.stringify(props.mainEstate.coordinates);

            if (mainEstateKey in markersMap) {
                const marker = markersMap[mainEstateKey];
                marker.className = 'main-estate-marker compared-estate-marker-border';
                marker.pointsToMainEstate = true;
            } else {
                markersMap[mainEstateKey] = {
                    titles: [],
                    coordinates: props.mainEstate.coordinates,
                    className: 'main-estate-marker',
                    pointsToMainEstate: true,
                };
            }

            return Object.values(markersMap);
        });

        const bounds = computed((): LatLngBounds => {
            const m = markers.value;

            if (m.length === 0) {
                return new LatLngBounds([51.1, 12], [48.5, 19]);
            }

            return new LatLngBounds(m.map(marker => marker.coordinates)).pad(0.5);
        });

        const center = computed(() => bounds.value.getCenter());
        const zoom = ref(10);

        // A single marker collapses bounds to a zero-size point, so fitBounds would zoom to the
        // layer maximum — cap it. With multiple markers fitBounds computes a sensible zoom itself.
        const fitBoundsOptions = computed(() => (markers.value.length <= 1 ? {maxZoom: 16} : {}));

        function createIcon(marker: EstatesMarker): DivIcon {
            const numberOfEstates = marker.pointsToMainEstate
                ? (marker.titles.length + 1)
                : marker.titles.length;
            const content = numberOfEstates === 1 ? '' : '<span>' + numberOfEstates + '</span>';

            return new DivIcon({
                className: '',
                iconAnchor: [0, 24],
                popupAnchor: [0, -36],
                html: `<span class="${marker.className}">${content}</span>`,
            });
        }

        function updateHeight(): void {
            const el = container.value as unknown as Element;

            maxPopupHeight.value = el.clientHeight * 0.8;
        }

        function redrawMap(): void {
            if (!leafletMap.value) {
                return;
            }

            leafletMap.value.invalidateSize();
            leafletMap.value.fitBounds(bounds.value, fitBoundsOptions.value);
        }

        // The map and its layers control are published for the overlay components mounted next
        // to the map (cadastre, flood zones); LMap and LControlLayers become ready independently.
        let pendingLayersControl: L.Control.Layers | null = null;

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;
            mapInstance.invalidateSize();
            mapInstance.fitBounds(bounds.value, fitBoundsOptions.value);
            viewFitted.value = true;

            if (pendingLayersControl) {
                publishLeafletMap(mapInstance.getContainer(), mapInstance, pendingLayersControl);
            }

            updateHeight();
        }

        function onLayersControlReady(layersControlInstance: L.Control.Layers): void {
            pendingLayersControl = layersControlInstance;

            if (leafletMap.value) {
                publishLeafletMap(leafletMap.value.getContainer(), leafletMap.value as L.Map, layersControlInstance);
            }
        }

        onMounted(() => {
            stopWatchingViewport = whenNearViewport(root.value!, () => {
                nearViewport.value = true;
            });
        });

        onBeforeUnmount(() => {
            stopWatchingViewport?.();

            if (leafletMap.value) {
                unpublishLeafletMap(leafletMap.value.getContainer());
            }
        });

        return {root, container, nearViewport, viewFitted, maxPopupHeight, markers, bounds, center, zoom, createIcon, updateHeight, onMapReady, onLayersControlReady};
    },
});
</script>
